import type { Synth } from './synth';
import type { Engine } from './engine';
import { MIDI_CHANNEL } from '../constants';

// Look-ahead and tick cadence for the FluidSynth-sequencer-driven scheduler.
// Lookahead must be > tick interval to keep the queue full. With sample-accurate
// internal scheduling, we can afford a relaxed cadence.
const LOOKAHEAD_SEC = 0.25;
const TICK_MS = 50;

export class Scheduler {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private nextWhenSec = 0;
  private running = false;
  private scheduledCount = 0;
  // Keys currently sounding (note-on scheduled, no note-off yet). Used to release
  // lingering notes at a variation boundary — some variations (e.g. the Aria) end
  // on a chord the source data never explicitly releases, which would otherwise
  // ring into the next piece.
  private readonly activeKeys = new Set<number>();

  constructor(
    private readonly ctx: AudioContext,
    private readonly synth: Synth,
    private readonly engine: Engine,
    private readonly onTick?: () => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.nextWhenSec = this.ctx.currentTime + 0.05;
    this.scheduledCount = 0;
    this.activeKeys.clear();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
    this.engine.setPlaying(true);
    this.tick();
  }

  stop(): void {
    this.running = false;
    if (this.intervalId !== null) clearInterval(this.intervalId);
    this.intervalId = null;
    this.flushFuture();
    this.engine.setPlaying(false);
  }

  pause(): void {
    if (!this.running) return;
    this.running = false;
    if (this.intervalId !== null) clearInterval(this.intervalId);
    this.intervalId = null;
    this.flushFuture();
    this.engine.setPlaying(false);
  }

  resume(): void {
    if (this.running) return;
    this.start();
  }

  /** Cancel all pending future events and silence active voices. */
  flushFuture(): void {
    this.synth.clearScheduled();
    this.synth.allNotesOff(MIDI_CHANNEL);
    this.activeKeys.clear();
  }

  private tick(): void {
    if (!this.running) return;
    const nowSec = this.ctx.currentTime;
    const horizon = nowSec + LOOKAHEAD_SEC;
    // Safety cap so a runaway sequence (e.g. infinite chord chain) can't pin the thread.
    let budget = 1024;
    while (this.nextWhenSec < horizon && budget-- > 0) {
      const evt = this.engine.popEvent();
      const offsetMs = Math.max(0, (this.nextWhenSec - nowSec) * 1000);
      this.synth.scheduleNote(MIDI_CHANNEL, evt.note.key, evt.note.velocity, offsetMs);
      if (evt.note.velocity > 0) this.activeKeys.add(evt.note.key);
      else this.activeKeys.delete(evt.note.key);
      if (this.scheduledCount < 30) {
        const tag = evt.note.velocity === 0 ? 'OFF' : 'ON ';
        console.log(
          `[sched] +${offsetMs.toFixed(1)}ms ${tag} key=${evt.note.key} vel=${evt.note.velocity} (var=${evt.varIdx} bar=${evt.barIdx} ev=${evt.eventIdx})`,
        );
      }
      this.scheduledCount++;
      this.nextWhenSec += evt.deltaSec;
      // Release notes still sounding from the outgoing variation, timed just before
      // the next variation's first note (1 ms before it, but never before this event)
      // so a note-off can't land after a shared key's fresh note-on and silence it.
      if (evt.variationChanged && this.activeKeys.size > 0) {
        const boundaryMs = Math.max(offsetMs, (this.nextWhenSec - nowSec) * 1000 - 1);
        for (const key of this.activeKeys) {
          this.synth.scheduleNote(MIDI_CHANNEL, key, 0, boundaryMs);
        }
        this.activeKeys.clear();
      }
    }
    this.onTick?.();
  }

  isRunning(): boolean {
    return this.running;
  }
}
